import { useMfaChallenge } from "@via/pages/authentication/mfa/mfa.api.tsx";
import MfaCodeScreen from "@via/pages/authentication/mfa/mfa-code-screen.tsx";
import {
  codesReminderPath,
  goTo,
  useFinishSignIn,
} from "@via/api/mfa-destination";
import MfaEnrollScreen from "@via/pages/authentication/mfa/mfa-enroll-screen.tsx";
import {
  ChallengeGone,
  LockedNotice,
  RecoveryCodesRemaining,
} from "@via/pages/authentication/mfa/mfa-notices.tsx";
import RecoveryCodesPanel from "@via/pages/authentication/mfa/recovery-codes-panel.tsx";
import { normalizeRedirectPath } from "@via/utils/post-login-redirect";
import type { MfaSessionResponse } from "common";
import { type JSX, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/** What stays on screen after the session exists, before leaving the page. */
type AfterSession =
  | { kind: "codes"; codes: string[]; destination: string }
  | { kind: "remaining"; remaining: number; destination: string };

/** The second factor step. The purpose comes from the challenge status call, never from the URL. */
export default function MfaPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const redirect = normalizeRedirectPath(searchParams.get("redirect"));
  const navigate = useNavigate();
  const finish = useFinishSignIn(redirect);
  const challenge = useMfaChallenge();
  const [after, setAfter] = useState<AfterSession | null>(null);

  const onSession = (response: MfaSessionResponse) => {
    const destination = finish(response);
    if (response.recoveryCodes) {
      setAfter({ kind: "codes", codes: response.recoveryCodes, destination });
    } else if (!response.codesAcknowledged) {
      navigate(codesReminderPath(destination));
    } else if (response.recoveryCodesRemaining !== undefined) {
      setAfter({
        kind: "remaining",
        remaining: response.recoveryCodesRemaining,
        destination,
      });
    } else {
      goTo(destination, navigate);
    }
  };

  let content: JSX.Element;
  if (after?.kind === "codes") {
    content = (
      <RecoveryCodesPanel
        codes={after.codes}
        onDone={() => goTo(after.destination, navigate)}
      />
    );
  } else if (after?.kind === "remaining") {
    content = (
      <RecoveryCodesRemaining
        remaining={after.remaining}
        onContinue={() => goTo(after.destination, navigate)}
      />
    );
  } else if (challenge.isLoading) {
    content = <p className="text-center text-sm text-gray-600">Loading</p>;
  } else if (challenge.isError || !challenge.data) {
    content = <ChallengeGone />;
  } else if (challenge.data.lockedUntil) {
    content = <LockedNotice until={challenge.data.lockedUntil} />;
  } else if (challenge.data.purpose === "signin") {
    content = <MfaCodeScreen status={challenge.data} onSession={onSession} />;
  } else {
    content = (
      <MfaEnrollScreen
        needsHandoff={challenge.data.purpose === "recovery_enroll"}
        onSession={onSession}
      />
    );
  }

  return (
    <div className="dotted flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-10 shadow sm:rounded-lg sm:px-12">
          {content}
        </div>
      </div>
    </div>
  );
}
